import { inject, Injectable } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';
import { TRANSLATION_BASE_URL } from '../tokens/base-url.token';

/**
 * Custom loader for Transloco to load translation files.
 *
 * This service is responsible for fetching translation files from a specified
 * base URL, which is injected dynamically through the `TRANSLATION_BASE_URL`
 * token. The loader fetches translations in JSON format based on the language
 * code provided.
 */
@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);
  private baseUrl = inject(TRANSLATION_BASE_URL);

  /**
   * Constructor for TranslocoHttpLoader service.
   *
   * @throws Error if the baseUrl is not provided.
   */
  constructor() {
    // Throw an error if no base URL is provided, as it's required for loading translations
    if (!this.baseUrl) {
      throw new Error(
        `TRANSLATION_BASE_URL is required but was not provided! To resolve this issue, ensure that you are providing the base URL for translation files through the \`provideLocalization()\` provider in your application configuration. For example, in your \`app.config.ts\` or a specific module, add the following: \n provideLocalization({ baseUrl: \`here goes your assets i18n route path on the project. Ex.: '/assets/i18n'\` }) \n This will specify the base URL where translation JSON files are located.`
      );
    }
  }

  /**
   * Retrieves the translation file for the specified language.
   *
   * This method constructs the URL for the translation file using the provided
   * language code and the base URL injected through the constructor.
   *
   * @param lang The language code for the translation file (e.g., 'en', 'es').
   * @returns An Observable of the translation JSON, which contains key-value pairs
   *          for the translations.
   */
  getTranslation(lang: string) {
    // To avoid cache different versions
    const version = new Date().getTime();
    // Return the HTTP GET request for fetching the translation JSON
    return this.http.get<Translation>(`${this.baseUrl}/${lang}.json?v=${version}`);
  }
}
